import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { IMPERSONATION_ERROR_CODE, IMPERSONATION_ERROR_I18N } from '@niko-crm/core/constants';
import { EMPTY, from, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ImpersonationService } from '../services/impersonation.service';
import { ScopedTokenRefreshService } from '../services/scoped-token-refresh.service';

/** 401 codes that mean "the impersonation session is dead": drop it and go back to the actor. */
const SESSION_ENDED_CODES: readonly string[] = [
  IMPERSONATION_ERROR_CODE.EXPIRED,
  IMPERSONATION_ERROR_CODE.REVOKED,
];

function impersonationEndedCode(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse) || err.status !== 401) return null;
  const message = (err.error as { message?: string | string[] } | null)?.message;
  const text = Array.isArray(message) ? message.join(' ') : (message ?? '');
  return SESSION_ENDED_CODES.find((code) => text.includes(code)) ?? null;
}

/**
 * Attaches the bearer token (impersonation > scoped > Amplify, see
 * `AuthService.getAccessToken`), reacts to a dead impersonation session, and
 * silently re-mints an expired tenant-scoped token (BR-AUTH-07).
 *
 * `401 IMPERSONATION_EXPIRED / _REVOKED` is NOT a logout: the actor's Cognito
 * session and scoped token are still valid. The session is dropped through
 * `ImpersonationService.exit()` (no `stop` call — the backend already closed
 * it), which leaves a one-shot notice for the toast and reloads as the actor.
 * The request itself completes empty: the reload is already on its way and a
 * re-thrown error would only add a second, misleading toast. This branch is
 * evaluated FIRST and unconditionally wins over the scoped-token-refresh
 * branch below.
 *
 * 403 `IMPERSONATION_ROUTE_FORBIDDEN / _NESTED` need nothing
 * here — `errorInterceptor` already toasts every 403 through
 * `BACKEND_ERROR_MAP`, which includes `IMPERSONATION_ERROR_I18N`.
 *
 * A plain `401` that is NOT an impersonation code is handled differently
 * depending on whether a scoped token exists:
 *   - No scoped token (admin app, or niko-crm before tenant selection): the
 *     error is re-thrown untouched — nothing here changes that path.
 *   - A scoped token exists and there is NO active impersonation: the scoped
 *     token (`niko_tenant_session`, signed with `expiresIn: '8h'`, never
 *     renewed — see `UsersAuthModule`) is the most likely cause, because the
 *     underlying Amplify/Cognito session normally outlives it. The request is
 *     re-minted via `ScopedTokenRefreshService.refresh()` (single-flight
 *     across parallel 401s) and retried EXACTLY ONCE with the new token.
 *   - A scoped token exists but the request is running UNDER IMPERSONATION
 *     (`ImpersonationService.getToken()` is non-null): re-minting is
 *     deliberately skipped. `POST /auth/select-tenant` is
 *     `@ForbiddenWhileImpersonating()`, and impersonation sessions are
 *     intentionally short-lived (15 min) with NO refresh path (BR-AUTH-03) —
 *     attempting one here would just trade one dead-token 401 for another.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const impersonation = inject(ImpersonationService);
  const scopedTokenRefresh = inject(ScopedTokenRefreshService);

  const attach = (token: string | null): HttpRequest<unknown> =>
    token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  // Per-request closure guard: this interceptor function runs once per HTTP
  // call, so `retried` cannot leak into an unrelated request. `next()` below
  // already points past THIS interceptor in the chain (Angular's functional
  // interceptors cannot recurse into themselves via `next`), so the retried
  // request's own failure could not loop back into this `catchError` even
  // without this flag — it is kept explicit anyway so the "retry exactly
  // once" contract does not silently depend on that pipeline shape.
  let retried = false;

  return from(authService.getAccessToken()).pipe(
    switchMap((token) => next(attach(token))),
    catchError((err: unknown) => {
      const impersonationCode = impersonationEndedCode(err);
      if (impersonationCode && impersonation.getToken()) {
        return from(
          impersonation.exit({
            notifyBackend: false,
            noticeKey: IMPERSONATION_ERROR_I18N[impersonationCode as keyof typeof IMPERSONATION_ERROR_I18N],
          }),
        ).pipe(switchMap(() => EMPTY));
      }

      const isExpiredScopedTokenCandidate =
        !retried &&
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !impersonation.getToken() &&
        !!authService.getScopedToken() &&
        // Defensive only: `ScopedTokenRefreshService` calls `/auth/select-tenant`
        // through `HttpBackend`, which never enters this interceptor chain to
        // begin with. This guards against that invariant changing later.
        !req.url.includes('/auth/select-tenant');

      if (!isExpiredScopedTokenCandidate) return throwError(() => err);

      retried = true;
      return scopedTokenRefresh.refresh().pipe(switchMap((newToken) => next(attach(newToken))));
    }),
  );
};
